import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import React, { ReactNode, useMemo } from 'react'
import Group from 'components/utils/Group'
import LeHotelMultiCartReviewGroup from './ReviewTiles/LeHotelMultiCartReviewGroup'
import BedbankMultiCartReviewGroup from './ReviewTiles/BedbankMultiCartReviewGroup'
import BedbankMultiCartReviewTile from './ReviewTiles/BedbankMultiCartReviewTile'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  isBedbankItem,
  isExperienceItem,
  isTransferItem,
  isLuxPlusSubscriptionItem,
  isSubscriptionJoinItem,
  isFlightItem,
  isInstantBookingLEHotelItem,
  isMerchandiseItem,
} from 'lib/checkout/checkoutUtils'
import ExperienceMultiCartReviewTile from './ReviewTiles/ExperienceMultiCartReviewTile'
import AirportTransferMultiCartReviewTile from './ReviewTiles/AirportTransferMultiCartReviewTile'
import FlightMultiCartReviewTile from './ReviewTiles/FlightMultiCartReviewTile'
import LuxPlusMultiCartReviewTile from './ReviewTiles/LuxPlusMultiCartReviewTile'
import { CartItemData } from './hooks/useAllUserMultiCartItemsMetadata'
import { groupBy } from 'lib/array/arrayUtils'
import MerchandiseMultiCartReviewTile from './ReviewTiles/MerchandiseMultiCartReviewTile'
import LeHotelMultiCartReviewTile from './ReviewTiles/LeHotelMultiCartReviewTile'
import MultiCartComparisonBanner from './MultiCartComparisonBanner'

const StyledHeadingTextBlock = styled(HeadingTextBlock)`
  padding-left: ${rem(16)};
  padding-bottom: ${rem(4)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

const StyledGroup = styled(Group)`
  margin-bottom: ${rem(12)};
`

interface Props {
  multiCartItems: Array<App.MultiCartItem>
  title?: string
  icon?: ReactNode
  cartItemsData: Array<CartItemData>
  tileVariant?: 'default' | 'condensed'
  id?: string
}

function UserMultiCartSection(props: Props) {
  const {
    multiCartItems,
    title,
    icon,
    cartItemsData,
    tileVariant = 'default',
    id,
  } = props

  const itemGroups = useMemo(() => {
    return groupBy(multiCartItems, (multiCartItem) => {
      const item = multiCartItem.item
      if (isBedbankItem(item) || isInstantBookingLEHotelItem(item)) {
        return `${item.offerId}-${item.checkIn}-${item.checkOut}`
      } else if (isMerchandiseItem(item)) {
        return `${item.advertId}-${item.variantId}`
      }
      return item.itemId
    })
  }, [multiCartItems])

  return <StyledGroup
    gap={12}
    tabletGap={tileVariant === 'condensed' ? 4 : 12}
    direction="vertical"
    horizontalAlign="stretch"
  >
    {title && icon && <StyledHeadingTextBlock startIcon={icon} variant="heading5">{title}</StyledHeadingTextBlock>}
    {id === 'accommodation' && <MultiCartComparisonBanner />}
    {Array.from(itemGroups.entries()).map(([key, value]) => {
      // Extract items from multiCartItems for compatibility checks
      const items = value.map(multiCartItem => multiCartItem.item)
      if (items.every(item => isMerchandiseItem(item))) {
        return <MerchandiseMultiCartReviewTile
          key={key}
          multiCartItems={value as Array<App.MultiCartMerchandiseItem>}
          primaryItem={items[0]}
          items={items}
          tileVariant={tileVariant}
        />
      }
      // Handle multi-room groups
      if (value.length > 1) {
        if (items.every(item => isBedbankItem(item))) {
          return <BedbankMultiCartReviewGroup
            key={key}
            multiCartItems={value as Array<App.MultiCartBedbankHotelItem>}
            tileVariant={tileVariant}
          />
        } else if (items.every(item => isInstantBookingLEHotelItem(item))) {
          return <LeHotelMultiCartReviewGroup
            key={key}
            multiCartItems={value as Array<App.MultiCartInstantBookingLEHotelItem>}
            tileVariant={tileVariant}
          />
        }
      }

      const item = items[0]

      if (isLuxPlusSubscriptionItem(item)) {
        const multiCartItem = value[0] as App.MultiCartLuxPlusSubscriptionItem
        return <LuxPlusMultiCartReviewTile
          key={multiCartItem.id}
          multiCartItem={multiCartItem}
          cartItemsData={cartItemsData}
          tileVariant={tileVariant}
        />
      } else if (isSubscriptionJoinItem(item)) {
        // Joining fee be shown within LuxPlusUserCartItem, no need for a separate tile
        return null
      } else if (isInstantBookingLEHotelItem(item)) {
        const multiCartItem = value[0] as App.MultiCartInstantBookingLEHotelItem
        return <LeHotelMultiCartReviewTile
          key={multiCartItem.id}
          multiCartItem={multiCartItem}
          tileVariant={tileVariant}
        />
      } else if (isBedbankItem(item)) {
        const multiCartItem = value[0] as App.MultiCartBedbankHotelItem
        return <BedbankMultiCartReviewTile
          key={multiCartItem.id}
          multiCartItem={multiCartItem}
          tileVariant={tileVariant}
        />
      } else if (isFlightItem(item)) {
        const multiCartItem = value[0] as App.MultiCartFlightItem
        return <FlightMultiCartReviewTile
          key={multiCartItem.id}
          multiCartItem={multiCartItem}
          tileVariant={tileVariant}
        />
      } else if (isExperienceItem(item)) {
        const multiCartItem = value[0] as App.MultiCartExperienceItem
        return <ExperienceMultiCartReviewTile
          key={multiCartItem.id}
          multiCartItem={multiCartItem}
          tileVariant={tileVariant}
        />
      }
      else if (isTransferItem(item)) {
        const multiCartItem = value[0] as App.MultiCartTransferItem
        return <AirportTransferMultiCartReviewTile
          key={multiCartItem.id}
          multiCartItem={multiCartItem}
          tileVariant={tileVariant}
        />
      }
      return null
    })}
  </StyledGroup>
}

export default UserMultiCartSection
